/**
 * CreditPaywallCard — kartu paywall saat saldo point habis (402 CreditRequired).
 *
 * Sebelumnya pesan "saldo habis" hanya tampil sebagai toast sesaat tanpa
 * jalan keluar; user harus mencari sendiri tab Belanja → Top up. Kartu ini
 * memberi CTA langsung "Top up sekarang" → /belanja/top-up (UX fix,
 * 2026-09-08). Non-blocking: bisa ditutup dengan ✕, swipe tetap bisa lanjut.
 */

import { Pressable, StyleSheet, Text, View } from 'react-native';

import { ThemedText } from '@/components/themed-text';
import { Brand, Radii, Spacing } from '@/constants/theme';

export type CreditPaywallCardProps = {
  /** Point yang dibutuhkan per worksheet (server: 402 CreditRequired.cost). */
  cost?: number;
  /** Saldo terakhir user (server: 402 CreditRequired.balance). */
  balance?: number;
  /** Buka halaman top-up. */
  onTopUp: () => void;
  /** Tutup kartu tanpa top-up. */
  onDismiss: () => void;
};

export function CreditPaywallCard({
  cost = 1,
  balance = 0,
  onTopUp,
  onDismiss,
}: CreditPaywallCardProps) {
  return (
    <View style={styles.card} accessibilityLiveRegion="polite">
      <View style={styles.headerRow}>
        <ThemedText style={styles.title}>Saldo point habis</ThemedText>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Tutup pesan saldo habis"
          onPress={onDismiss}
          style={({ pressed }) => [styles.closeBtn, pressed && styles.closeBtnPressed]}
        >
          <Text style={styles.closeBtnText}>✕</Text>
        </Pressable>
      </View>
      <ThemedText type="small" themeColor="textSecondary">
        {cost} point diperlukan untuk setiap worksheet pilihan. Saldo Bunda {balance} point —
        top up dulu yuk, prosesnya cepat.
      </ThemedText>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel="Top up sekarang"
        accessibilityHint="Buka halaman pilihan paket point"
        onPress={onTopUp}
        style={({ pressed }) => [styles.cta, pressed && styles.ctaPressed]}
      >
        <Text style={styles.ctaText}>Top up sekarang</Text>
      </Pressable>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#ffffff',
    borderWidth: 1,
    borderColor: '#e5e5e5',
    borderRadius: Radii.medium,
    padding: Spacing.three,
    gap: Spacing.two,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    color: Brand.primary,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f3ee',
  },
  closeBtnPressed: {
    opacity: 0.6,
  },
  closeBtnText: {
    fontSize: 13,
    color: '#6b6573',
    fontWeight: '700',
  },
  cta: {
    alignSelf: 'flex-start',
    backgroundColor: Brand.primary,
    borderRadius: Radii.pill,
    paddingVertical: Spacing.two,
    paddingHorizontal: Spacing.four,
  },
  ctaPressed: {
    opacity: 0.85,
  },
  ctaText: {
    color: '#ffffff',
    fontSize: 13,
    fontWeight: '700',
  },
});
